import { pick } from 'lodash';
import { get } from 'lodash/fp';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { hasFeatureFlags } from 'utils/features';
import { Icon } from 'ui';
import { UniversalUploader } from 'components/shared/UniversalUploader';
import ImagePreview from 'components/shared/ImagePreview';
import InfoTooltip from 'components/shared/InfoTooltip';
import ImageLibraryPicker from 'components/shared/ImageLibraryPicker';
import request from 'utils/request_ajax';
import { routes as ROUTES } from 'constants/config';
import fileStackImageUpload from 'misc/imageUpload';
import FileStackImage from 'misc/filestack_image';
import ImageLibraryAddDialog from 'components/shared/ImageLibraryAddDialog';
import Section, { SectionHeader } from './Section';
import * as actions from '../../../actions/pathBuilder';

const LinkButton = ({ className = '', ...props }) => (
  <button
    type="button"
    className={`u-linkBtn fz-base fw-n d-b ${className}`}
    {...props}
  />
);

class PathImage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      viewImageLibrary: false,
      addToLibraryVisible: false,
      hasFileUploadFF: hasFeatureFlags([
        'learning-file-stack-upload-replacement',
      ]),
      universalUploaderModalVisible: false,
    };
    this.handleUploadImage = this.handleUploadImage.bind(this);
    this.saveBssUpload = this.saveBssUpload.bind(this);
    this.handleRemoveImage = this.handleRemoveImage.bind(this);
    this.handleImageLibrarySelect = this.handleImageLibrarySelect.bind(this);
    this.toggleImageLibrary = this.toggleImageLibrary.bind(this);
    this.toggleAddToLibrary = this.toggleAddToLibrary.bind(this);
  }

  closeUniversalUploaderModal = () => {
    if (this.state.universalUploaderModalVisible) {
      this.setState({ universalUploaderModalVisible: false });
    }
  };

  toggleUniversalUploader = () => {
    this.setState(state => ({
      universalUploaderModalVisible: !state.universalUploaderModalVisible,
    }));
  };

  handleUploadImage() {
    const associatedImageParams = {
      content_id: this.props.pathId,
      content_type: this.props.contentType,
      image_type: this.props.imageType,
    };

    if (this.state.hasFileUploadFF) {
      this.toggleUniversalUploader();
    } else {
      fileStackImageUpload(associatedImageParams)
        .then(image => {
          this.props.updatePath({ image });
          this.toggleAddToLibrary();
        })
        .catch(e => {
          console.error(e);
          if (this.state.addToLibraryVisible) {
            this.toggleAddToLibrary();
          }
        });
    }
  }

  saveBssUpload(newUpload) {
    return new Promise(async (resolve, reject) => {
      const { bssUploadId } = newUpload;

      try {
        const uploadImage = await request.postJSON(ROUTES.IMAGES, {
          image: {
            bss_upload_id: bssUploadId,
            content_id: this.props.pathId,
            content_type: this.props.contentType,
            image_type: this.props.imageType,
          },
        });
        this.props.updatePath({ image: uploadImage });
        this.closeUniversalUploaderModal();
        this.toggleAddToLibrary();
        resolve();
      } catch (e) {
        reject(new Error(e));
      }
    });
  }

  handleRemoveImage() {
    const associatedImageParams = {
      image: {
        content_id: this.props.pathId,
        content_type: this.props.contentType,
        image_type: this.props.imageType,
      },
    };

    request
      .delete(ROUTES.IMAGE(this.props.image.id), associatedImageParams)
      .then(() => {
        this.props.updatePath({ image: null });
      });
  }

  toggleImageLibrary() {
    this.setState(state => ({ viewImageLibrary: !state.viewImageLibrary }));
  }

  toggleAddToLibrary() {
    this.setState(state => ({
      addToLibraryVisible: !state.addToLibraryVisible,
    }));
  }

  handleImageLibrarySelect(selectedImage) {
    request
      .post(ROUTES.ASSOCIATED_IMAGES, {
        content_id: this.props.pathId,
        content_type: this.props.contentType,
        image_type: this.props.imageType,
        image_id: selectedImage.id,
      })
      .then(image => {
        this.props.updatePath({ image });
        this.setState({ viewImageLibrary: false });
      });
  }

  render() {
    const { isArchived, image, isCertification } = this.props;
    let filestackImage = null;
    if (image) {
      filestackImage = new FileStackImage({
        ...pick(image, ['signature', 'policy', 'handle']),
        transform: {
          resize: { width: 232, height: 300, fit: 'max' },
        },
      });
    }

    return (
      <Section>
        {this.state.viewImageLibrary && (
          <ImageLibraryPicker
            onSelect={this.handleImageLibrarySelect}
            onDismiss={this.toggleImageLibrary}
          />
        )}
        <SectionHeader className="d-f jc-sb">
          <span>
            <span className="mr-4">Cover Image</span>
            <InfoTooltip
              tooltip={
                isCertification
                  ? t(
                      'certifications.certification_builder.sidebar.cover_image_tooltip',
                    )
                  : t('paths.path_builder.cover_image_tooltip')
              }
            />
          </span>
        </SectionHeader>
        {!isArchived && image ? (
          <div>
            <img
              className="coverImage"
              src={image.bssSignedUrl || filestackImage.url}
              alt={image.name}
            />
            <ImagePreview
              image={image}
              imageType={this.props.imageType}
              message="Preview"
              className="mr-8"
              isCertification={isCertification}
            />
            <LinkButton
              className="pull-right u-linkBtn fz-base fw-n"
              onClick={this.handleRemoveImage}
            >
              <Icon name="delete" className="mr-2" /> Remove
            </LinkButton>
          </div>
        ) : (
          <div>
            <LinkButton onClick={this.handleUploadImage}>
              <Icon name="add" className="mr-2" /> Upload
            </LinkButton>
            {this.props.features.allowImageLibrary && (
              <LinkButton className="mt-8" onClick={this.toggleImageLibrary}>
                <Icon name="add" className="mr-2" />{' '}
                {I18n.t('image_library.select_from_library')}
              </LinkButton>
            )}
          </div>
        )}
        {this.state.universalUploaderModalVisible && (
          <UniversalUploader
            accept={['image/*']}
            displayInModal
            modalShown={this.state.universalUploaderModalVisible}
            onModalDismiss={this.closeUniversalUploaderModal}
            onComplete={this.saveBssUpload}
            helperText={t('universal_uploader.image_library_helper')}
            deleteUploadRecord={false}
          />
        )}
        <ImageLibraryAddDialog
          canManageContentLibrary={
            this.props.permissions.can_manage_content_library
          }
          image={this.props.image}
          visible={this.state.addToLibraryVisible}
          toggleVisible={this.toggleAddToLibrary}
        />
      </Section>
    );
  }
}

const mapStateToProps = ({ pathBuilder, ...state }) => {
  return {
    image: pathBuilder.image,
    pathId: pathBuilder.id,
    imageType: state.meta.coverImage.imageType,
    contentType: state.meta.coverImage.contentType,
    permissions: state.meta.permissions,
    isCertification: !!get('certification')(pathBuilder),
    features: {
      allowImageLibrary: state.meta.features.allowImageLibrary,
    },
  };
};

const mapDispatchToProps = {
  updatePath: actions.updatePath,
};

export default connect(mapStateToProps, mapDispatchToProps)(PathImage);
